import React, { useEffect, useState, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { LogOnMount } from 'react-amplitude-hooks';
import api from 'Api';
import { ROUTES } from 'Router/Routes';
import useHandleError from 'Utils/handleError';
import { AuthContext } from 'States/auth/authState';
import { KycContext } from 'States/kyc/kycState';
import { UiContext } from "States/ui/uiState";
import { Button } from 'Components/shared/buttons';
import Fade from 'Components/shared/Fade';
import Header from 'Components/shared/Header';
import LoadingSpinner from 'Components/shared/LoadingSpinner';
import KycLevelInfoBox from 'Components/shared/KycLevelInfoBox';
import { Tile } from "Components/shared/Tile";
import OrderInformation from './OrderInformation';
import PurchaseSummary from './PurchaseSummary';
import UserInformation from 'Components/UserDetails/UserInformation';
import AddressInformation from './AddressInformation';
import CollectMethodEnum from 'Enums/CollectMethod';
import i18nextTranslate from 'Lang/i18nextTranslate';
import { i18nextKeys } from 'Lang/i18nextKeys';

const OrderDetails = ({ match, isAdmin = false }) => {
  const { isKycEnabled, currentKycTier } = useContext(KycContext);
  const handleError = useHandleError();
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(true);
  const [purchaseOrder, setPurchaseOrder] = useState({});
  const [orderUserKycTier, setOrderUserKycTier] = useState(0);
  const {
    user: {
      profile: { sub },
    },
  } = useContext(AuthContext);
  const { id } = match.params;
  const {
    breakpoints: { md, xl }
  } = useContext(UiContext);

  useEffect(() => {
    (async () => {
      try {
        const purchaseOrderRes = await api.PurchaseOrder.details(id);
        /* TODO: this should be prevented via filter and/or backend
            because the response for every order can still be seen via network tab
        */
        if (!isAdmin && purchaseOrderRes.UserId !== sub) {
          return history.push(ROUTES.orders);
        }
        setPurchaseOrder(purchaseOrderRes);
        if (isAdmin) {
          try {
            const { TierNumber } = await api.Kyc.getUserKycData({
              user: purchaseOrderRes.UserId,
              expand: false
            });
            setOrderUserKycTier(TierNumber);
          } catch (error) {
            if (error.response?.status === 404) {
              return;
            } else {
              handleDataError(error);
            }
          }
        }
      } catch (error) {
        handleDataError(error);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [id, history]);

  const handleDataError = (error) => {
    const message = i18nextTranslate(
      i18nextKeys.errorPurchaseManagementDetailsLoadingError
    );
    handleError({ error, message });
  };

  const navigateToOrderList = () => history.push(
    isAdmin
      ? ROUTES.admin.orders
      : ROUTES.orders
  );

  const openInvoice = async () => {
    try {
      const data = await api.PurchaseOrder.getInvoice(id);
      const link = URL.createObjectURL(data);
      window.open(link, '_blank');
    } catch (error) {
      const message = i18nextTranslate(
        i18nextKeys.errorPurchaseManagementDetailsDownloadInvoiceFail
      );
      handleError({ error, message });
    }
  };

  const downloadPdf = (
    <Button
      text={i18nextTranslate(i18nextKeys.orderCheckSentInvoice)}
      onClick={openInvoice}
      dataQa="purchaseOrderDetails-openInvoice"
      width={{
        md: "302px",
        xl: "100%",
        default: "100%"
      }}
      secondary
    />
  );

  return (
    <LogOnMount
      eventType="Order details"
      eventProperties={{
        screen: isAdmin ? "order management" : "account"
      }}
    >
      <Fade show={true}>
        <Header
          text={i18nextTranslate(i18nextKeys.orderDetails)}
          onArrowBackClick={navigateToOrderList}
          arrowBack
        />
        <div
          className="w-full flex flex-col xl:flex-row gap-16 md:gap-24 justify-center color-8 m-auto"
        >
          {isLoading ? (
            <LoadingSpinner />
          ) : (
            <>
              <div className="flex flex-col gap-16 md:gap-24 w-full xl:w-2/3">
                {!xl && downloadPdf}
                <OrderInformation
                  order={purchaseOrder}
                  setOrder={setPurchaseOrder}
                  isAdmin={isAdmin}
                />
                <PurchaseSummary
                  order={purchaseOrder}
                  setOrder={setPurchaseOrder}
                  isAdmin={isAdmin}
                />
              </div>
              <div className="flex flex-col gap-16 md:gap-24 w-full xl:w-1/3">
                {xl && downloadPdf}
                <Tile
                  title={i18nextTranslate(i18nextKeys.commonInfoUser)}
                  expandable
                >
                  <div className="flex flex-col gap-32">
                    <UserInformation
                      user={purchaseOrder.User}
                      singleColumn={!md || xl}
                      showFirstName
                      showLastName
                      showEmail
                    />
                    {isKycEnabled && (
                      <KycLevelInfoBox
                        kycTierNumber={isAdmin ? orderUserKycTier : currentKycTier}
                        isLoading={false}
                        dataQa="order-user-kycTier"
                        fullWidth={!md || xl}
                      />
                    )}
                  </div>
                </Tile>
                <div className="flex flex-col md:flex-row xl:flex-col gap-16 md:gap-24">
                  {purchaseOrder.InvoiceAddress && (
                    <AddressInformation
                      title={i18nextTranslate(i18nextKeys.orderInvoiceInformation)}
                      address={purchaseOrder.InvoiceAddress}
                      dataQa="order-invoice"
                    />
                  )}
                  {purchaseOrder.ShippingAddress &&
                    purchaseOrder.CollectMethod === CollectMethodEnum.values.TokenCard && (
                    <AddressInformation
                      title={i18nextTranslate(i18nextKeys.orderShippingInformation)}
                      address={purchaseOrder.ShippingAddress}
                      dataQa="order-shipping"
                    />
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </Fade>
    </LogOnMount>
  );
};

export default OrderDetails;
